import {
  HTMLAttributes,
  memo,
  useState,
  useRef,
  useEffect,
  lazy,
  Suspense,
  MutableRefObject,
  useMemo,
} from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button, Tabs } from 'antd';
import Icon, { CloseOutlined } from '@ant-design/icons';
import { useVoucherDetailShow, useSetVoucherDetailShow } from 'hooks/business/voucherDetail';
import { useIsSbsMode } from 'hooks/business/useIsSbs';
import { eventBus } from 'services/eventBus';
import { useSideRightDisplay } from 'hooks/business/conversationsHooks/useConversationSide';
import { PreviewModalProvider } from './PreviewModal';
import { ReactComponent as BotIcon } from './assets/bot.svg';
import Head from './Head';
import NavToPlatform from './NavToPlatform';
import { StoreContext } from './useInvoice';
import { ToDoBundleProvider } from './ToDoBundle/context';
import TodoBadge from './ToDoBundle/Badge';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { isQuickChat } from 'productLine';
import { useSetAtom } from 'jotai';
import {
  useNewTodoDrawerVisible,
  useOpenNewTodoDrawer,
} from 'views/Conversation/Sider/ToDoBundle/NewTodo';
import { TodoContextProvider } from 'views/Aide/Basis/Todo/Context';
import { Loading } from 'views/BootGuide/components/Blink';
import { render } from 'components/Render';
import posthog from '@leyan/lytics';
import { todoStatus, todoCurrentId, locateTodoId, RelatedInfo } from './ToDoBundle/state';
import { useBuyerFAQContext } from 'views/Mercado/FAQ/BuyerFAQ';
import { useChatConversationContext } from '../Chat/contexts/ChatConversationContext';
import { usePhraseSideModeValue, useSetPhraseSideMode } from '../Chat/components/Phrase/state';
import { getTodoCache } from './ToDoBundle/TodoCache';
import { useLatest } from 'ahooks';
import { SplitLine } from 'components/SplitLine';
import { ChatSideTabKey, getChannelBaseTab } from 'hooks/business/chat/useChatSide';
import { SIDE_WIDTH_CACHE_KEY, SIDE_MAX_WIDTH, SIDE_MIN_WIDTH } from './constants';

const Container = styled.section`
  --main-color: var(--fett-app-primary-color);
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  position: relative;
  background: var(--fett-bg-panel);
  color: var(--fett-text-primary);
  overflow: hidden;
  .${({ theme }) => theme.getAntdPrefixedClassName('tabs')} {
    flex: 0 0 38px;
    background: var(--fett-conv-sider-head-bg);
    &-tab {
      margin: 0 10px;
      .${({ theme }) => theme.getAntdPrefixedClassName('tabs-tab-btn')} {
        &:hover,
        &:active,
        &:focus {
          color: var(--fett-app-primary-color) !important;
        }
      }
    }
    &-tab-active {
      .${({ theme }) => theme.getAntdPrefixedClassName('tabs-tab-btn')} {
        color: var(--fett-app-primary-color) !important;
      }
    }
    &-ink-bar {
      background-color: var(--fett-app-primary-color);
    }
    &-tab-btn {
      > div > span {
        margin-right: 4px;
        font-size: 14px;
      }
    }
  }

  [data-color-mode='dark'] & {
    .${({ theme }) => theme.getAntdPrefixedClassName('tabs')} {
      color: var(--fett-text-primary);

      &-nav::before {
        border-bottom-color: var(--fett-border-default);
      }

      &-tab {
        .${({ theme }) => theme.getAntdPrefixedClassName('tabs-tab-btn')} {
          &:hover,
          &:active,
          &:focus {
            color: var(--fett-text-primary) !important;
          }
        }
      }

      &-tab-active {
        .${({ theme }) => theme.getAntdPrefixedClassName('tabs-tab-btn')} {
          color: var(--fett-text-primary) !important;
        }
      }
    }
  }

  > section {
    flex: 1 0 0;
    position: relative;
    overflow: hidden;
    > * {
      height: 100%;
    }
  }
`;

const tabComponentMap = {
  trade: lazy(() => import('./Trade')),
  product: lazy(() => import('./Product')),
  voucher: lazy(() => import('./Voucher')),
  todo: lazy(() => import('./Todo')),
  buyer: lazy(() => import('./FacebookBuyer')),
  buyerActivity: lazy(() => import('./BuyerActivity')),
};

const tabIconMap = {
  reply: <Icon component={BotIcon} />,
  trade: null,
  product: null,
  voucher: null,
  todo: null,
  buyer: null,
  buyerActivity: null,
};

export interface SiderProps extends HTMLAttributes<HTMLDivElement> {
  conversation: Conversation.Conversation;
  facebookBuyer?: Conversation.FacebookBuyer;
}

function Sider(props: SiderProps) {
  const { conversation, facebookBuyer, ...otherProps } = props;

  const isSbsMode = useIsSbsMode();

  const { conversationId, channel } = conversation;

  const { t } = useTranslate();

  const ref = useRef<HTMLElement>(null);
  const comRef = useRef<any>(null);

  const [activeTabKey, setActiveTabKey] = useState<ChatSideTabKey>('trade');
  const lastManualActiveTabKey = useRef<ChatSideTabKey>('trade');
  const { visible: todoDrawerVisible } = useNewTodoDrawerVisible();
  const setTodoStatus = useSetAtom(todoStatus);
  const setTodoCurrentId = useSetAtom(todoCurrentId);
  const setLocateTodoId = useSetAtom(locateTodoId);
  const open = useOpenNewTodoDrawer();

  const channelBaseTab = getChannelBaseTab(channel);

  useEffect(() => {
    if (todoDrawerVisible) {
      setActiveTabKey('todo');
      lastManualActiveTabKey.current = 'todo';
    }
  }, [todoDrawerVisible]);

  const { whiteListConfig: { TODO_MANAGEMENT_CENTER = false } = {} } = useOrgConfigStateValue();

  const voucherDetailShow = useVoucherDetailShow();

  useEffect(() => {
    if (voucherDetailShow) {
      setActiveTabKey('voucher');
      lastManualActiveTabKey.current = 'voucher';
    }
  }, [voucherDetailShow]);

  const setVoucherDetailShow = useSetVoucherDetailShow();

  useEffect(() => {
    if (activeTabKey !== 'voucher') {
      setVoucherDetailShow(false);
    } else if (channel === 'TOKOPEDIA') {
      setActiveTabKey('trade');
      lastManualActiveTabKey.current = 'trade';
    }
  }, [activeTabKey, setVoucherDetailShow, channel]);

  useEffect(() => {
    const handler = (trade: Conversation.Trade.TradeWithLogistics) => {
      // 触发订单详情
      setActiveTabKey('trade');
      lastManualActiveTabKey.current = 'trade';
      requestAnimationFrame(() => {
        comRef?.current?.openDetail?.(trade);
      });
    };
    eventBus.on('CONV_OPEN_SIDE_ORDER_DETAIL', handler);
    return () => {
      eventBus.off('CONV_OPEN_SIDE_ORDER_DETAIL', handler);
    };
  }, []);

  useEffect(() => {
    if (getTodoCache(conversation.storeId, conversation.externalBuyerId)) {
      setActiveTabKey('todo');
      lastManualActiveTabKey.current = 'todo';
      open();
    }
  }, [conversation.storeId, conversation.externalBuyerId, open]);
  const conversationRef = useLatest(conversation);
  useEffect(() => {
    if (activeTabKey === 'todo') {
      if (getTodoCache(conversationRef.current.storeId, conversationRef.current.externalBuyerId)) {
        open();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTabKey]);

  useEffect(() => {
    const handler = (todo: RelatedInfo) => {
      if (activeTabKey === 'todo') {
        if (todo) {
          setTodoStatus(String(todo.status) as any);
          setTodoCurrentId(todo.todoId);
        }
      } else {
        setActiveTabKey('todo');
        lastManualActiveTabKey.current = 'todo';
        setTodoStatus(String(todo.status) as any);
        setLocateTodoId(todo.todoId);
      }
    };
    eventBus.on('RELATED_MSGID_OPEN_TODO', handler);
    return () => {
      eventBus.off('RELATED_MSGID_OPEN_TODO', handler);
    };
  }, [setTodoStatus, setTodoCurrentId, activeTabKey, setLocateTodoId]);

  useEffect(() => {
    const handler = (todo: Todo.TodoInstance) => {
      setActiveTabKey('todo');
      requestAnimationFrame(() => {
        setTodoStatus(`${todo.todoStatus}`);
        setLocateTodoId(todo.id);
        setTodoCurrentId(todo.id);
      });
    };
    eventBus.on('SIDER_TODO_LOCATE', handler);
    return () => {
      eventBus.off('SIDER_TODO_LOCATE', handler);
    };
  }, [setTodoStatus, setTodoCurrentId, setLocateTodoId]);

  const tabList: { key: ChatSideTabKey; label: string }[] = useMemo(() => {
    const labels: Record<ChatSideTabKey, string> = {
      trade: t('scene.chatWorkspace.sidebar.tab.orders'),
      product: t('scene.chatWorkspace.sidebar.tab.products'),
      voucher: t('scene.chatWorkspace.sidebar.tab.coupons'),
      todo: t('scene.chatWorkspace.sidebar.tab.tasks'),
      buyer: t('scene.chatWorkspace.sidebar.tab.buyerDetails'),
      buyerActivity: t('scene.chatWorkspace.sidebar.tab.buyerActivity'),
    };
    if (isSbsMode) {
      return [
        { key: 'trade', label: labels.trade },
        { key: 'product', label: labels.product },
      ];
    }
    if (TODO_MANAGEMENT_CENTER) {
      return [
        ...channelBaseTab.map((key) => ({ key, label: labels[key] })),
        { key: 'todo', label: labels.todo },
      ];
    }
    return channelBaseTab.map((key) => ({ key, label: labels[key] }));
  }, [isSbsMode, TODO_MANAGEMENT_CENTER, channelBaseTab, t]);

  useEffect(() => {
    if (!tabList.some((tab) => tab.key === activeTabKey)) {
      setActiveTabKey(tabList[0].key);
      lastManualActiveTabKey.current = tabList[0].key;
    }
  }, [tabList, activeTabKey]);

  useEffect(() => {
    if (conversation.conversationId && channelBaseTab.length > 0) {
      posthog.capture('$noChannelOrUnknownChannel', {
        conversationId: conversation.conversationId,
        channel: conversation.channel,
      });
    }
  }, [channelBaseTab.length, conversation]);

  const { show } = useBuyerFAQContext();

  const { phraseModal } = useChatConversationContext();
  const phraseKey = 'pharse';
  const closePhraseKey = 'closePhrase';

  const phraseSideMode = usePhraseSideModeValue();
  const setPhraseSideMode = useSetPhraseSideMode();

  return (
    <Container ref={ref} {...otherProps}>
      <ToDoBundleProvider
        value={{
          conversationId: conversationId,
          storeId: conversation.storeId,
          buyerId: conversation.buyerId,
          externalBuyerId: conversation.externalBuyerId,
          platform: channel as any,
          assistantId: conversation.assistantId,
          region: conversation.region,
          conversation,
        }}
      >
        <Tabs
          size="small"
          activeKey={phraseModal?.visible ? phraseKey : activeTabKey}
          onChange={(key) => {
            if (key === phraseKey) {
              phraseModal?.show(phraseModal?.meta);
            } else {
              phraseModal?.hide();
              if (key === closePhraseKey) {
                setPhraseSideMode(false);
              } else {
                setActiveTabKey(key as ChatSideTabKey);
                lastManualActiveTabKey.current = key as ChatSideTabKey;
              }
            }
          }}
          tabBarExtraContent={
            channel === 'MERCADO' ? (
              <Button
                size="small"
                type="text"
                onClick={() =>
                  show({
                    buyerId: conversation.externalBuyerId.split('_')[0],
                    storeId: conversation.storeId,
                    activeKey: 'trade',
                  })
                }
              >
                {t('scene.chatWorkspace.sidebar.tab.relatedInfo')}
              </Button>
            ) : null
          }
        >
          {tabList.map((tab) => {
            const { [tab.key]: icon } = tabIconMap;
            if (tab.key === 'todo' && !isQuickChat) {
              return (
                <Tabs.TabPane
                  key={tab.key}
                  tab={
                    <TodoBadge>
                      <div>
                        {icon}
                        {tab.label}
                      </div>
                    </TodoBadge>
                  }
                />
              );
            }

            return (
              <Tabs.TabPane
                key={tab.key}
                tab={
                  <div>
                    {icon}
                    {tab.label}
                  </div>
                }
              />
            );
          })}
          {phraseSideMode && (
            <Tabs.TabPane key={phraseKey} tab={t('scene.chatWorkspace.sidebar.tab.quickReplies')} />
          )}
          {phraseSideMode && (
            <Tabs.TabPane
              key={closePhraseKey}
              tab={<CloseOutlined style={{ margin: '-12px' }} />}
            />
          )}
        </Tabs>
        <section>
          <PreviewModalProvider>
            <Suspense
              fallback={
                <div className="flex items-center justify-center">
                  <Loading />
                </div>
              }
            >
              {render<{
                key: string;
                conversation: Conversation.Conversation;
                ref: MutableRefObject<any>;
              }>({
                as: tabComponentMap[activeTabKey],
                key: conversationId,
                conversation,
                ref: comRef,
              })}
            </Suspense>
            {phraseModal?.visible && (
              <div
                style={{
                  position: 'absolute',
                  top: '0',
                  width: '100%',
                  background: 'var(--fett-bg-panel)',
                  zIndex: 2,
                }}
              >
                {phraseModal?.meta}
              </div>
            )}
          </PreviewModalProvider>
        </section>
        <NavToPlatform conversation={conversation} />
      </ToDoBundleProvider>
    </Container>
  );
}

const MemorizedSider = memo(Sider);

const defaultWidth = localStorage.getItem(SIDE_WIDTH_CACHE_KEY) || SIDE_MIN_WIDTH;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${defaultWidth}px;
  height: 100%;
  overflow: hidden;
  font-family: Arial, Helvetica, sans-serif;
  color: var(--fett-text-primary);
  > head {
    flex: 0 0 auto;
    margin-bottom: 16px;
  }
`;

interface SiderEntryProps extends HTMLAttributes<HTMLDivElement> {
  conversation?: Conversation.Conversation;
  facebookBuyer?: Conversation.FacebookBuyer;
}

export function ConversationSider({ conversation, facebookBuyer, ...otherProps }: SiderEntryProps) {
  const sideRightDisplay = useSideRightDisplay();
  const ref = useRef<HTMLDivElement>(null);

  if (!conversation || !sideRightDisplay) {
    return null;
  }

  return (
    <Wrapper ref={ref} {...otherProps}>
      <SplitLine
        type="vertical"
        position="left"
        target={ref}
        max={SIDE_MAX_WIDTH}
        min={SIDE_MIN_WIDTH}
        onDone={(newValue) => {
          localStorage.setItem(SIDE_WIDTH_CACHE_KEY, newValue.toString());
        }}
      />
      <StoreContext.Provider
        value={{
          buyerNick: conversation.buyerNick,
          storeId: conversation.storeId,
          storeName: conversation.storeName,
        }}
      >
        <Head conversation={conversation} facebookBuyer={facebookBuyer} />
        <TodoContextProvider>
          <MemorizedSider facebookBuyer={facebookBuyer} conversation={conversation} />
        </TodoContextProvider>
      </StoreContext.Provider>
    </Wrapper>
  );
}

export default ConversationSider;
